import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Loader2 } from "lucide-react";
import { adminGet, uploadChecklist, checklistInfo } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (user === null) return;                       // still checking session
    if (!user || user.role !== "admin") { nav("/login", { state: { from: "/admin" } }); return; }
    Promise.all([adminGet("leads"), adminGet("bookings"), adminGet("stats"), adminGet("payments")])
      .then(([l, b, s, p]) => { setLeads(l); setBookings(b); setStats(s); setPayments(p); setReady(true); })
      .catch(() => nav("/login", { state: { from: "/admin" } }));
    checklistInfo().then(setChecklist).catch(() => {});
  }, [user, nav]);

  if (user === null || !ready) {
    return <div className="flex min-h-screen items-center justify-center bg-[#050505]"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  }

  const rows = tab === "leads" ? leads : tab === "bookings" ? bookings : payments;
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="font-display text-3xl font-light text-[#f5f5f5]">BGM <span className="text-gold">Admin</span></Link>
          <div className="flex items-center gap-4 font-body text-sm text-[#a3a3a3]">
            <span>Leads <b className="text-gold">{stats?.leads}</b></span>
            <span>Bookings <b className="text-gold">{stats?.bookings}</b></span>
            <span>Subscribers <b className="text-gold">{stats?.subscribers}</b></span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 hover:border-[#d4af37]/50 hover:text-gold"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>

        <div className="glass mt-6 rounded-2xl p-6" data-testid="checklist-upload-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg text-[#f5f5f5]">Credit Clarity Checklist file</h2>
              <p className="mt-1 font-body text-sm text-[#a3a3a3]">
                {checklist?.available
                  ? <>Current file: <span className="text-gold">{checklist.filename}</span> — visitors download this on the homepage.</>
                  : "No file uploaded yet. Visitors get the built-in text checklist until you upload one."}
              </p>
            </div>
            <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm font-semibold ${uploading ? "bg-[#555] text-white" : "bg-[#d4af37] text-[#050505] hover:bg-[#f3e5ab]"}`}>
              {uploading ? "Uploading…" : "Upload / Replace file"}
              <input type="file" className="hidden" data-testid="checklist-file-input" disabled={uploading}
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.csv"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setUploading(true);
                  try {
                    const fd = new FormData(); fd.append("file", f);
                    const res = await uploadChecklist(fd);
                    setChecklist({ available: true, filename: res.filename });
                  } catch { alert("Upload failed. Try again."); }
                  finally { setUploading(false); e.target.value = ""; }
                }} />
            </label>
          </div>
        </div>

        <div className="mt-8 flex gap-2">
          {["leads", "bookings", "payments"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`rounded-full px-5 py-2 font-body text-sm capitalize ${tab === t ? "bg-[#d4af37] text-[#050505]" : "glass text-[#c9c9c9]"}`}>{t}</button>
          ))}
        </div>

        <div className="glass mt-6 overflow-x-auto rounded-2xl">
          <table className="w-full text-left font-body text-sm">
            <thead className="border-b border-white/10 text-[#888]">
              <tr>
                {tab === "leads"
                  ? ["Name", "Email", "Phone", "Interest", "Message", "Date"].map((h) => <th key={h} className="p-4">{h}</th>)
                  : tab === "bookings"
                  ? ["Name", "Email", "Service", "Date", "Time", "Notes"].map((h) => <th key={h} className="p-4">{h}</th>)
                  : ["Plan", "Email", "Amount", "Status", "Date"].map((h) => <th key={h} className="p-4">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="admin-table">
              {rows.map((r) => (
                <tr key={r.id || r.session_id} className="border-b border-white/5 text-[#c9c9c9]">
                  {tab === "leads"
                    ? [<td key="n" className="p-4">{r.name}</td>, <td key="e" className="p-4">{r.email}</td>, <td key="p" className="p-4">{r.phone || "—"}</td>, <td key="i" className="p-4 text-gold">{r.interest || "—"}</td>, <td key="m" className="p-4">{r.message || "—"}</td>, <td key="d" className="p-4">{r.created_at?.slice(0, 10)}</td>]
                    : tab === "bookings"
                    ? [<td key="n" className="p-4">{r.name}</td>, <td key="e" className="p-4">{r.email}</td>, <td key="s" className="p-4 text-gold">{r.service}</td>, <td key="pd" className="p-4">{r.preferred_date}</td>, <td key="pt" className="p-4">{r.preferred_time}</td>, <td key="no" className="p-4">{r.notes || "—"}</td>]
                    : [<td key="pl" className="p-4 text-gold">{r.plan}</td>, <td key="e" className="p-4">{r.email || "—"}</td>, <td key="a" className="p-4">${r.amount}</td>, <td key="st" className="p-4">{r.payment_status}</td>, <td key="d" className="p-4">{r.created_at?.slice(0, 10)}</td>]}
                </tr>
              ))}
              {rows.length === 0 && <tr><td className="p-6 text-[#666]" colSpan={6}>No {tab} yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
